import { Alert, Avatar, Divider, Spin, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeft, UserRound, UserRoundCog } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AddMembers from '../../components/AddMembers';
import ChatInput from '../../components/ChatInput';
import MessageBubble from '../../components/MessageBubble';
import { getGroupById, getGroupMembers } from '../../redux/actions/groups';
import { getGroupMessages } from '../../redux/actions/message';
import { getRoutes } from './../../constants/Routes';
import { decodeId } from '../../utils/secureId';
import { getInitials } from '../../utils';
import { bannerMessages } from '../../constants';

const GroupDetails = () => {
    const navigate = useNavigate();
    const { groupId: group_id } = useParams();
    const groupId = group_id && decodeId(group_id);
    const { groupMessages: messages, groupMessagesPagination: pagination } = useSelector(
        (state) => state.messageSlice
    );
    const { activeGroup, members } = useSelector((state) => state.groupSlice);

    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const getMoreMessages = () => {
        if (pagination.page < pagination?.totalPages && !loading) {
            setLoading(true);
            getGroupMessages(groupId, false, pagination?.page + 1).finally(() => {
                setLoading(false);
            });
        }
    };

    useEffect(() => {
        if (groupId) {
            getGroupById(groupId);
            getGroupMembers(groupId);
            getGroupMessages(groupId);
        }
    }, [groupId]);

    const groupedMessages = useMemo(() => {
        return messages?.reduce((acc, message) => {
            const dateKey = dayjs(message.createdAt).format('YYYY-MM-DD');
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(message);
            return acc;
        }, {});
    }, [messages]);

    return (
        <div className="GROUP_CHAT_DETAIL">
            <div className="Chat__Header">
                <div className="header-left">
                    <ArrowLeft
                        size={20}
                        className="back-btn"
                        onClick={() => navigate(getRoutes().groups.route())}
                    />
                    <div className="info-container">
                        <div className="info">
                            <div className="subtitle">
                                <Typography.Text ellipsis={{ tooltip: true }}>
                                    {activeGroup?.name || activeGroup?.phone || 'Unknown'}
                                </Typography.Text>
                            </div>
                            {activeGroup && (
                                <div className="title">{activeGroup?.members?.length || 0} members</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="header-right">
                    {!!members?.length && (
                        <div className="info-container">
                            <Avatar.Group
                                maxCount={5}
                                maxPopoverTrigger="hover"
                                maxStyle={{ color: '#f56a00', backgroundColor: '#fde3cf' }}
                            >
                                {members?.map((member) => (
                                    <Tooltip key={member.id} title={member.name}>
                                        <Avatar>{getInitials(member.name) || <UserRound size={18} />}</Avatar>
                                    </Tooltip>
                                ))}
                            </Avatar.Group>
                            <Divider className="custom-divider" type="vertical" />
                        </div>
                    )}
                    <UserRoundCog cursor="pointer" onClick={() => setIsOpen(true)} />
                </div>
            </div>
            <div
                id="scrollableDiv"
                style={{
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    height: '500px',
                }}
                className="chat-messages"
            >
                <InfiniteScroll
                    dataLength={messages?.length || 0}
                    next={getMoreMessages}
                    hasMore={pagination?.page < pagination?.totalPages}
                    inverse={true}
                    scrollableTarget="scrollableDiv"
                >
                    {loading && (
                        <div className="chat-loader">
                            <p>Loading more messages...</p>
                            <Spin size="small" />
                        </div>
                    )}
                    {pagination?.totalPages > 1 && pagination?.page === pagination?.totalPages && (
                        <Alert message="You've reached the end of the chat history! 🎉" closable showIcon />
                    )}
                    {groupedMessages &&
                        Object.keys(groupedMessages).map((date) => (
                            <div key={date}>
                                <div className="date-separator-container">
                                    <div className="date">
                                        {dayjs(date).isSame(dayjs(), 'day')
                                            ? 'Today'
                                            : dayjs(date).format('DD/MM/YYYY')}
                                    </div>
                                </div>
                                {groupedMessages[date].map(
                                    (message) =>
                                        message?.type !== 0 && (
                                            <MessageBubble key={message.id} message={message} isGroup />
                                        )
                                )}
                            </div>
                        ))}
                </InfiniteScroll>
            </div>

            {!members?.length && (
                <Alert
                    key={groupId}
                    message={bannerMessages?.NO_MEMBERS}
                    banner
                    closable
                    showIcon={false}
                />
            )}

            <ChatInput isGroup members={activeGroup?.members} isBlocked={!members?.length} />

            {isOpen && <AddMembers open={isOpen} onCancel={() => setIsOpen(false)} group={activeGroup} />}
        </div>
    );
};

export default GroupDetails;
